import { ITransportService } from "../../TransportServices"
import { ITransportServicePricesFormValues, ITSPSeason } from "./NewItem"
import { TComparisonSheetData, TFileData, TFilePrice, TSeason } from "./store"
import { formatMoneyByDecimal } from "@sembark-travel/money"

export function transformServiceOption(service: ITransportService) {
	return {
		...service,
		label: service.service,
	}
}
export function transformDataToFormValues(fileDetails: TFileData) {
	const { seasons, prices, trip_destinations } = fileDetails
	const data: ITransportServicePricesFormValues = {
		trip_destinations: trip_destinations,
		currency: seasons[0]?.currency,
		timezone_offset: seasons[0]?.timezone_offset,
		seasons: seasons.map((season, index) => ({
			season_id: index,
			season_name: season.name,
			date_ranges: season.intervals.map(({ start_date, end_date }) => ({
				start_date: new Date(start_date),
				end_date: new Date(end_date),
			})),
			cab_types: season.cab_types,
		})),
		transport_price_services: prices.map((price) => ({
			transport_service_location: price.transport_service_location,
			transport_service_types: price.transport_services.map((service) => ({
				transport_service_type: transformServiceOption(
					service.transport_service
				),
				seasons: seasons.map((season, index) => {
					return season.cab_types.map((cab) => {
						const cabPriceData =
							service?.seasons?.[season.id]?.prices?.[cab.id]?.prices
						const formattedPrice = cabPriceData
							? formatMoneyByDecimal(cabPriceData)
							: undefined
						return {
							season_id: index,
							cab_type_id: cab.id,
							price: Number(formattedPrice) || undefined,
						}
					})
				}),
			})),
		})),
	}
	return data
}

export function updatePriceSeasons({
	currentValues,
	seasonValue,
}: {
	currentValues: ITransportServicePricesFormValues["transport_price_services"]
	seasonValue: ITSPSeason[]
}) {
	return currentValues.map((service) => {
		return {
			...service,
			transport_service_types: service.transport_service_types.map((type) => {
				// Convert existing seasons to a Map for faster lookup
				const seasonMaps = new Map<number, Map<number, number | undefined>>()
				if (type.seasons) {
					type.seasons.forEach((seasonArray) => {
						seasonArray.forEach((p) => {
							if (!p.cab_type_id) return

							if (!seasonMaps.has(p.season_id)) {
								seasonMaps.set(p.season_id, new Map())
							}
							seasonMaps
								.get(p.season_id)
								?.set(p.cab_type_id, p.price || undefined)
						})
					})
				}

				return {
					...type,
					seasons: seasonValue.map((season) => {
						const seasonId = season.season_id
						const cabPrices = seasonMaps.get(seasonId) || new Map()

						return season.cab_types.map((cabType) => ({
							season_id: seasonId,
							cab_type_id: cabType.id,
							price: cabPrices.get(cabType.id) || undefined,
						}))
					}),
				}
			}),
		}
	})
}

function compareTransportServices(
	oldServices: TComparisonSheetData["prices"][number]["transport_services"],
	newServices: TComparisonSheetData["prices"][number]["transport_services"]
) {
	const diffResult: TComparisonSheetData["prices"][number]["transport_services"] =
		[]
	const newServiceMap = new Map(
		newServices.map((service) => [service.transport_service.id, service])
	)

	oldServices.forEach((OldService) => {
		const foundService = newServiceMap.get(OldService.transport_service.id)
		const sessionDiff: TFilePrice["transport_services"][0]["seasons"] = {}

		if (foundService) {
			Object.keys(OldService.seasons || {}).forEach((seasonId) => {
				const oldSeason = OldService.seasons?.[seasonId]
				const newSeason = foundService.seasons?.[seasonId]
				if (oldSeason && newSeason) {
					const oldPrices = oldSeason.prices || {}
					const newPrices = { ...newSeason.prices }
					Object.keys(oldPrices).forEach((cabTypeId) => {
						if (!newPrices[cabTypeId]) {
							newPrices[cabTypeId] = {
								...oldPrices[cabTypeId],
								prices: { ...oldPrices[cabTypeId].prices, status: "removed" },
							}
						} else if (
							oldPrices[cabTypeId].prices.amount !==
							newPrices[cabTypeId].prices.amount
						) {
							newPrices[cabTypeId].prices.status = "modified"
							newPrices[cabTypeId].prices.oldValue = oldPrices[cabTypeId].prices
						}
					})

					Object.keys(newPrices).forEach((cabTypeId) => {
						if (!oldPrices[cabTypeId]) {
							newPrices[cabTypeId] = {
								...newPrices[cabTypeId],
								prices: { ...newPrices[cabTypeId].prices, status: "added" },
							}
						}
					})

					sessionDiff[seasonId] = { ...oldSeason, prices: newPrices }
				} else {
					const prices2 = oldSeason?.prices || {}
					sessionDiff[seasonId] = {
						season_id: oldSeason?.season_id || 0,
						prices: prices2,
						status: "removed",
					}
				}
			})

			Object.keys(foundService.seasons || {}).forEach((seasonId) => {
				if (!OldService.seasons?.[seasonId]) {
					sessionDiff[seasonId] = {
						...foundService.seasons?.[seasonId],
						season_id: foundService.seasons?.[seasonId].season_id || 0,
						status: "added",
						prices: foundService.seasons?.[seasonId].prices || null,
					}
				}
			})

			diffResult.push({ ...OldService, seasons: sessionDiff })
		} else {
			diffResult.push({ ...OldService, status: "removed" })
		}
	})

	newServices.forEach((service) => {
		if (
			!oldServices.some(
				(OldService) =>
					OldService.transport_service.id === service.transport_service.id
			)
		) {
			diffResult.push({ ...service, status: "added" })
		}
	})
	return diffResult
}

export function compareTransportFiles(
	latestVersion: TComparisonSheetData,
	oldVersion: TComparisonSheetData
) {
	const priceDiffResult: TComparisonSheetData["prices"] = []
	const seasonDiffResult: TSeason[] = []

	const latestSeasonsMap = new Map(
		latestVersion.seasons.map((season) => [
			JSON.stringify([season.name, season.intervals]),
			season,
		])
	)
	const oldSeasonsMap = new Map(
		oldVersion.seasons.map((season) => [
			JSON.stringify([season.name, season.intervals]),
			season,
		])
	)
	oldVersion.seasons.forEach((oldSeason) => {
		const key = JSON.stringify([oldSeason.name, oldSeason.intervals])
		const newSeason = latestSeasonsMap.get(key)
		if (newSeason) {
			latestVersion.prices.forEach((location) => {
				location.transport_services.forEach((service) => {
					if (service.seasons?.[newSeason.id]) {
						service.seasons[oldSeason.id] = { ...service.seasons[newSeason.id] }
						if (oldSeason.id !== newSeason.id) {
							delete service.seasons[newSeason.id]
						}
					}
				})
			})
			// if new cab is added
			const combinedUniqueCabs = [
				...new Map(
					[...newSeason.cab_types, ...oldSeason.cab_types].map((item) => [
						item.id,
						item,
					])
				).values(),
			]
			seasonDiffResult.push({ ...oldSeason, cab_types: combinedUniqueCabs })
		} else {
			seasonDiffResult.push({ ...oldSeason, status: "removed" })
		}
	})

	latestVersion.seasons.forEach((newSeason) => {
		const key = JSON.stringify([newSeason.name, newSeason.intervals])
		const oldSeason = oldSeasonsMap.get(key)
		if (!oldSeason) {
			seasonDiffResult.push({ ...newSeason, status: "added" })
		}
	})

	const oldPricesMap = new Map(
		oldVersion.prices.map((price) => [
			price.transport_service_location.id,
			price,
		])
	)

	latestVersion.prices.forEach((newPrice) => {
		const oldPrice = oldPricesMap.get(newPrice.transport_service_location.id)
		if (oldPrice) {
			const transportServiceDiff = compareTransportServices(
				oldPrice.transport_services,
				newPrice.transport_services
			)
			priceDiffResult.push({
				...oldPrice,
				transport_services: transportServiceDiff,
			})
		} else {
			priceDiffResult.push({ ...newPrice, status: "added" })
		}
	})

	oldVersion.prices.forEach((oldPrice) => {
		if (
			!latestVersion.prices.some(
				(newPrice) =>
					newPrice.transport_service_location.id ===
					oldPrice.transport_service_location.id
			)
		) {
			priceDiffResult.push({ ...oldPrice, status: "removed" })
		}
	})

	const DataFile = { seasons: seasonDiffResult, prices: priceDiffResult }
	// console.log(DataFile)
	return DataFile
}
